import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeart,
  faList,
  faChartColumn,
  faDownload,
  faShieldHalved,
  faSpinner,
  faFileLines,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import PatientResultsService from '../../services/PatientResultsService';
import { API_BASE_URL } from '../../constants/api';
import './PatientResults.css';

const FLAG_BADGES = {
  normal: { label: 'In Range', tone: 'in-range' },
  borderline: { label: 'Borderline', tone: 'borderline' },
  high: { label: 'High', tone: 'out' },
  low: { label: 'Low', tone: 'out' },
};

const fmtDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const pct = (v, { min, max }) => Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100));

function RangeGauge({ result }) {
  const { scale, numericValue } = result;
  if (!scale || numericValue === null || numericValue === undefined) {
    return <p className="dh-results__gauge-empty">No reference range available for this marker.</p>;
  }
  const ticks = scale.segments.slice(1).map((s) => s.from);
  return (
    <div>
      <div className="dh-results__gauge-track">
        {scale.segments.map((s) => (
          <span
            key={`${s.from}-${s.to}`}
            className={`dh-results__gauge-seg dh-results__gauge-seg--${s.flag}`}
            style={{ left: `${pct(s.from, scale)}%`, width: `${pct(s.to, scale) - pct(s.from, scale)}%` }}
          />
        ))}
        <span
          className={`dh-results__gauge-marker dh-results__gauge-marker--${result.flag}`}
          style={{ left: `${pct(numericValue, scale)}%` }}
          title={`${result.value} ${result.units}`}
        />
      </div>
      <div className="dh-results__gauge-ticks">
        {ticks.map((t) => (
          <span key={t} style={{ left: `${pct(t, scale)}%` }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

function ChartView({ results }) {
  return (
    <div className="dh-results__chart">
      {results.map((r) => {
        const badge = FLAG_BADGES[r.flag];
        return (
          <div className="dh-results__chart-row" key={r.biomarker}>
            <div className="dh-results__chart-head">
              <strong>{r.name}</strong>
              <span className="dh-results__chart-value">{r.value} <small>{r.units}</small></span>
              {badge && <span className={`dh-results__badge dh-results__badge--${badge.tone}`}>{badge.label}</span>}
            </div>
            <RangeGauge result={r} />
          </div>
        );
      })}
      <div className="dh-results__legend">
        <span><i className="dh-results__gauge-seg--normal" /> In range</span>
        <span><i className="dh-results__gauge-seg--borderline" /> Borderline</span>
        <span><i className="dh-results__gauge-seg--high" /> Out of range</span>
      </div>
    </div>
  );
}

function ListView({ results }) {
  return (
    <div className="dh-results__list">
      <div className="dh-results__list-head">
        <span>Marker</span>
        <span>Result</span>
        <span>Reference Range</span>
        <span>Status</span>
      </div>
      {results.map((r) => {
        const badge = FLAG_BADGES[r.flag];
        return (
          <div className="dh-results__row" key={r.biomarker}>
            <div className="dh-results__row-name">
              <strong>{r.name}</strong>
              {r.about && <span>{r.about}</span>}
            </div>
            <div className="dh-results__row-value">
              <strong>{r.value}</strong>
              <span>{r.units}</span>
            </div>
            <div className="dh-results__row-range">{r.range || '—'}</div>
            <div>
              {badge
                ? <span className={`dh-results__badge dh-results__badge--${badge.tone}`}>{badge.label}</span>
                : <span className="dh-results__row-range">—</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function reportMeta(report) {
  return [
    ['Collected', report.collectedAt],
    ['Received by lab', report.sampleReceivedAt],
    ['Reported', report.resultsReadyAt],
  ]
    .filter(([, d]) => d)
    .map(([label, d]) => `${label} ${fmtDate(d)}`)
    .concat(report.orderingPhysician ? [`Ordering physician: ${report.orderingPhysician}`] : [])
    .join(' · ');
}

function Notice({ icon, title, children }) {
  return (
    <div className="dh-results__card dh-results__note">
      <div className="dh-results__note-icon"><FontAwesomeIcon icon={icon} /></div>
      <div>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}

const PatientResults = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('loading'); // loading | error | expired | ok
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await PatientResultsService.getResults(token);
      if (res?.success) {
        setData(res.data);
        setStatus('ok');
      } else if ((res?.message || '').toLowerCase().includes('expired')) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus(err?.status === 410 ? 'expired' : 'error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const [view, setView] = useState('chart');

  const firstName = (data?.patient?.firstName || '').trim();
  const shell = (subtitle, content) => (
    <div className="dh-results">
      <div className="dh-results__container dh-results__brand">
        <FontAwesomeIcon icon={faHeart} /> Black Heart Association
      </div>
      <section className="dh-results__container dh-results__hero">
        <span className="dh-results__eyebrow">Results Portal</span>
        <h1 className="dh-results__title">Your heart health, at a glance</h1>
        {subtitle && <p className="dh-results__sub">{subtitle}</p>}
      </section>
      <section className="dh-results__container dh-results__body">
        {content}
        <p className="dh-results__disclaimer">
          <FontAwesomeIcon icon={faShieldHalved} /> Reference ranges are general adult guidelines. These results are
          for informational purposes and are not a medical diagnosis. Always consult your physician.
        </p>
      </section>
    </div>
  );

  if (loading) {
    return shell(null, (
      <div className="dh-results__loading">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading your results…
      </div>
    ));
  }

  if (status === 'expired') {
    return shell(null, (
      <Notice icon={faClock} title="This link has expired">
        <p>Please contact us for a new link to view your results.</p>
      </Notice>
    ));
  }

  if (status === 'error' || !data) {
    return shell(null, (
      <Notice icon={faFileLines} title="Results not found">
        <p>This link is invalid. Please double-check the link or contact us.</p>
      </Notice>
    ));
  }

  const { status: kitStatus = {}, results = [], summary = {} } = data;
  const pdfUrl = `${API_BASE_URL}/patient-results/${encodeURIComponent(token)}/pdf`;
  const meta = reportMeta(data);
  const subtitle = firstName ? `Hi ${firstName}, here are your blood test results.` : 'Here are your blood test results.';

  if (kitStatus.phase !== 'ready') {
    return shell(subtitle, (
      <div className="dh-results__card dh-results__note" style={{ justifyContent: 'space-between' }}>
        <div>
          <h3>{data.title}</h3>
          <p>{kitStatus.message || 'Your results are not ready yet.'}</p>
          {meta && <p className="dh-results__meta">{meta}</p>}
        </div>
        <span className={`dh-results__badge dh-results__badge--phase-${kitStatus.phase}`}>{kitStatus.label || 'Pending'}</span>
      </div>
    ));
  }

  return shell(subtitle, (
    <article>
      <header className="dh-results__report-head">
        <div>
          <h2 className="dh-results__report-title">{data.title}</h2>
          {meta && <p className="dh-results__meta">{meta}</p>}
        </div>
        <span className="dh-results__badge dh-results__badge--phase-ready">{kitStatus.label}</span>
      </header>

      <div className="dh-results__summary">
        <div className="dh-results__summary-item">
          <strong className="dh-results__summary-value dh-results__summary-value--good">{summary.inRange || 0}</strong>
          <span>Markers in range</span>
        </div>
        <div className="dh-results__summary-item">
          <strong className="dh-results__summary-value dh-results__summary-value--watch">{summary.borderline || 0}</strong>
          <span>Markers borderline</span>
        </div>
        <div className="dh-results__summary-item">
          <strong className="dh-results__summary-value dh-results__summary-value--bad">{summary.outOfRange || 0}</strong>
          <span>Markers out of range</span>
        </div>
      </div>

      <div className="dh-results__toggle" role="group" aria-label="Results view">
        <button type="button" className={view === 'list' ? 'is-active' : ''} aria-pressed={view === 'list'} onClick={() => setView('list')}>
          <FontAwesomeIcon icon={faList} /> List
        </button>
        <button type="button" className={view === 'chart' ? 'is-active' : ''} aria-pressed={view === 'chart'} onClick={() => setView('chart')}>
          <FontAwesomeIcon icon={faChartColumn} /> Chart
        </button>
      </div>

      {view === 'chart' ? <ChartView results={results} /> : <ListView results={results} />}

      <div className="dh-results__actions">
        <a className="dh-results__btn" href={pdfUrl}>
          <FontAwesomeIcon icon={faDownload} /> Download PDF
        </a>
      </div>
    </article>
  ));
};

export default PatientResults;
